import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import usePartMutations from "../../data/usePartMutations";
import Modal from "../../layouts/Modal";
import { IPart, PartStatus } from "../../models/Part";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface SendToQcFormFields {
  qcReason: string;
}

interface SendToQcModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  parts: IPart[];
  onCompleted?: () => void;
  onFailed?: () => void;
}

export default function SendToQcModal({
  open,
  setOpen,
  parts,
  onCompleted,
  onFailed,
}: SendToQcModalProps) {
  const { t } = useTranslation("stock");
  const { patchPart } = usePartMutations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset } = useForm<SendToQcFormFields>({
    defaultValues: { qcReason: "" },
  });

  const partsToUpdate = useMemo(
    () => parts.filter((part) => part.id && part.partStatus !== PartStatus.QC),
    [parts]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const defaultReason = parts.length === 1 ? parts[0].qcReason ?? "" : "";
    reset({ qcReason: defaultReason });
  }, [open, parts, reset]);

  const onConfirm = handleSubmit(async (data) => {
    if (partsToUpdate.length === 0) {
      setOpen(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await Promise.all(
        partsToUpdate.map((part) =>
          patchPart.mutateAsync({
            id: part.id,
            partStatus: PartStatus.QC,
            qcReason: data.qcReason,
          })
        )
      );

      onCompleted?.();
      setOpen(false);
    } catch (_error) {
      onFailed?.();
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-xl">
      <>
        <p className="text-xl font-semibold">{t("Send to QC")}</p>
        <div className="w-full flex flex-col gap-y-4 my-4">
          <p className="text-sm text-gray-600">
            {partsToUpdate.length === 1
              ? t("You are about to send 1 part to QC.")
              : t("You are about to send {{count}} parts to QC.", { count: partsToUpdate.length })}
          </p>
          {partsToUpdate.length === 0 ? (
            <p className="text-sm text-amber-700">
              {t("All selected parts are already in QC.")}
            </p>
          ) : (
            <TextInput
              richText
              rows={5}
              label={t("QC Reason Message")}
              register={register}
              registerName={"qcReason"}
              rounded
            />
          )}
        </div>
        <div className="flex flex-row min-w-full gap-x-4">
          <ButtonInput
            label={t("Cancel")}
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes={"flex-grow !bg-gray-500 hover:!bg-gray-600 disabled:!bg-gray-400"}
            disabled={isSubmitting}
          />
          <ButtonInput
            label={t("Confirm")}
            isSubmit={false}
            onClick={onConfirm}
            classes={"flex-grow"}
            isLoading={isSubmitting}
            disabled={isSubmitting || partsToUpdate.length === 0}
          />
        </div>
      </>
    </Modal>
  );
}
